// Admin catalog management — the categories customers shop by and the cities
// LakayCart serves. The marketplace runs on this; seeded data was just the
// bootstrap. In-use rows can't be deleted (the API blocks it) — deactivate.

import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Loader2, Plus, Trash2, Tag, MapPin, ShoppingCart, FileText } from 'lucide-react';
import { toast } from 'sonner';
import { PortalShell } from '../../components/shared/PortalShell';
import { Card, SectionHeader, EmptyState } from '../../components/shared/primitives';
import { Button } from '../../components/stripe-ui/Button';
import { ADMIN_NAV } from '../_navs';
import { admin, type AdminCategory, type AdminCity } from '../../lib/api';

export function AdminCatalogPage() {
  return (
    <PortalShell nav={ADMIN_NAV} activeId="catalog" title="Catalog" subtitle="Categories customers shop by and cities you serve.">
      <div className="space-y-6">
        <Categories />
        <Cities />
      </div>
    </PortalShell>
  );
}

// ─── Categories ──────────────────────────────────────────────

function Categories() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'categories'], queryFn: admin.categories });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'categories'] });

  const [slug, setSlug] = useState('');
  const [label, setLabel] = useState('');
  const [mode, setMode] = useState<'cart' | 'text'>('cart');

  const create = useMutation({
    mutationFn: () => admin.createCategory({ slug: slug.trim(), label: label.trim(), mode, active: true }),
    onSuccess: () => { setSlug(''); setLabel(''); invalidate(); toast.success('Category added'); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });
  const toggle = useMutation({
    mutationFn: (c: AdminCategory) => admin.updateCategory(c.id, { active: !c.active }),
    onSuccess: invalidate,
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });
  const remove = useMutation({
    mutationFn: (id: number) => admin.deleteCategory(id),
    onSuccess: () => { invalidate(); toast.success('Category deleted'); },
    onError: (e: Error) => toast.error(e.message || 'Cannot delete'),
  });

  return (
    <Card padding="p-0">
      <div className="p-5 border-b border-slate-100">
        <SectionHeader title="Service categories" subtitle="What customers shop by. 'Cart' shows an item grid; 'Text' is a free-form request." />
      </div>

      {/* Add row */}
      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-end gap-2 bg-slate-50/60">
        <Field label="Slug (immutable)" value={slug} onChange={(v) => setSlug(v.toLowerCase().replace(/[^a-z0-9_]/g, ''))} placeholder="electronics" w="w-36" />
        <Field label="Label" value={label} onChange={setLabel} placeholder="Electronics" w="w-44" />
        <label className="block">
          <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">Mode</span>
          <select value={mode} onChange={(e) => setMode(e.target.value as 'cart' | 'text')} className="mt-1 block px-2 py-1.5 text-[13px] border border-slate-200 rounded-md bg-white">
            <option value="cart">Cart</option><option value="text">Text</option>
          </select>
        </label>
        <Button size="sm" onClick={() => create.mutate()} loading={create.isPending} disabled={!slug.trim() || !label.trim()} leadingIcon={<Plus className="w-3.5 h-3.5" />}>Add</Button>
      </div>

      {q.isLoading ? <Loading /> : (q.data ?? []).length === 0 ? <EmptyState icon={Tag} title="No categories" body="Add the first one above." /> : (
        <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
          <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
            <tr><th className="text-left py-2.5 px-5 font-medium">Category</th><th className="text-left py-2.5 px-3 font-medium">Mode</th><th className="text-left py-2.5 px-3 font-medium">Usage</th><th className="text-left py-2.5 px-3 font-medium">Active</th><th className="text-right py-2.5 px-5 font-medium"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(q.data ?? []).map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="py-2.5 px-5"><span className="font-semibold text-slate-900">{c.label}</span><span className="ml-2 text-[11px] font-mono text-slate-400">{c.slug}</span></td>
                <td className="py-2.5 px-3"><span className="inline-flex items-center gap-1 text-[12px] text-slate-600">{c.mode === 'cart' ? <ShoppingCart className="w-3.5 h-3.5" /> : <FileText className="w-3.5 h-3.5" />} {c.mode}</span></td>
                <td className="py-2.5 px-3 text-[12px] text-slate-500">{c.item_count} items · {c.request_count} reqs</td>
                <td className="py-2.5 px-3"><Toggle on={c.active} onClick={() => toggle.mutate(c)} /></td>
                <td className="py-2.5 px-5 text-right">
                  <button onClick={() => { if (window.confirm(`Delete "${c.label}"?`)) remove.mutate(c.id); }} disabled={c.item_count > 0 || c.request_count > 0} title={c.item_count > 0 || c.request_count > 0 ? 'In use — deactivate instead' : 'Delete'} className="p-1 rounded text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:hover:text-slate-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ─── Cities ──────────────────────────────────────────────────

function Cities() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'cities'], queryFn: admin.cities });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['admin', 'cities'] });

  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');

  const create = useMutation({
    mutationFn: () => admin.createCity({ name: name.trim(), region: region.trim() || null, country: 'HT', active: true, latitude: lat ? Number(lat) : null, longitude: lng ? Number(lng) : null }),
    onSuccess: () => { setName(''); setRegion(''); setLat(''); setLng(''); invalidate(); toast.success('City added'); },
    onError: (e: Error) => toast.error(e.message || 'Failed'),
  });
  const toggle = useMutation({ mutationFn: (c: AdminCity) => admin.updateCity(c.id, { active: !c.active }), onSuccess: invalidate, onError: (e: Error) => toast.error(e.message) });
  const remove = useMutation({ mutationFn: (id: number) => admin.deleteCity(id), onSuccess: () => { invalidate(); toast.success('City deleted'); }, onError: (e: Error) => toast.error(e.message || 'Cannot delete') });

  return (
    <Card padding="p-0">
      <div className="p-5 border-b border-slate-100">
        <SectionHeader title="Cities served" subtitle="Where VoyeLakay operates. Coordinates power nearest-merchant routing." />
      </div>

      <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-end gap-2 bg-slate-50/60">
        <Field label="City name" value={name} onChange={setName} placeholder="Jacmel" w="w-40" />
        <Field label="Region" value={region} onChange={setRegion} placeholder="Sud-Est" w="w-36" />
        <Field label="Latitude" value={lat} onChange={setLat} placeholder="18.234" w="w-28" />
        <Field label="Longitude" value={lng} onChange={setLng} placeholder="-72.534" w="w-28" />
        <Button size="sm" onClick={() => create.mutate()} loading={create.isPending} disabled={!name.trim()} leadingIcon={<Plus className="w-3.5 h-3.5" />}>Add</Button>
      </div>

      {q.isLoading ? <Loading /> : (q.data ?? []).length === 0 ? <EmptyState icon={MapPin} title="No cities" body="Add one above." /> : (
        <table className="text-[13px] w-full block overflow-x-auto whitespace-nowrap sm:table sm:whitespace-normal">
          <thead className="text-[10.5px] uppercase tracking-wider text-slate-400 bg-slate-50">
            <tr><th className="text-left py-2.5 px-5 font-medium">City</th><th className="text-left py-2.5 px-3 font-medium">Coordinates</th><th className="text-left py-2.5 px-3 font-medium">Usage</th><th className="text-left py-2.5 px-3 font-medium">Active</th><th className="text-right py-2.5 px-5 font-medium"></th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {(q.data ?? []).map((c) => (
              <tr key={c.id} className="hover:bg-slate-50">
                <td className="py-2.5 px-5"><span className="font-semibold text-slate-900">{c.name}</span>{c.region && <span className="ml-2 text-[11px] text-slate-400">{c.region}</span>}</td>
                <td className="py-2.5 px-3 font-mono text-[11px] text-slate-500">{c.latitude && c.longitude ? `${Number(c.latitude).toFixed(3)}, ${Number(c.longitude).toFixed(3)}` : '—'}</td>
                <td className="py-2.5 px-3 text-[12px] text-slate-500">{c.merchant_count} merchants · {c.recipient_count} recipients</td>
                <td className="py-2.5 px-3"><Toggle on={c.active} onClick={() => toggle.mutate(c)} /></td>
                <td className="py-2.5 px-5 text-right">
                  <button onClick={() => { if (window.confirm(`Delete "${c.name}"?`)) remove.mutate(c.id); }} disabled={c.merchant_count > 0 || c.recipient_count > 0} title={c.merchant_count > 0 || c.recipient_count > 0 ? 'In use — deactivate instead' : 'Delete'} className="p-1 rounded text-slate-400 hover:text-rose-600 disabled:opacity-30 disabled:hover:text-slate-400">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ─── bits ────────────────────────────────────────────────────

function Loading() { return <div className="text-center py-10 text-slate-400 text-[13px]"><Loader2 className="inline w-4 h-4 animate-spin" /> Loading…</div>; }

function Field({ label, value, onChange, placeholder, w }: { label: string; value: string; onChange: (v: string) => void; placeholder?: string; w: string }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-semibold text-slate-500">{label}</span>
      <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className={`mt-1 ${w} px-2 py-1.5 text-[13px] border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-brand-500/30`} />
    </label>
  );
}

function Toggle({ on, onClick }: { on: boolean; onClick: () => void }) {
  return (
    <button onClick={onClick} className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${on ? 'bg-emerald-500' : 'bg-slate-300'}`} aria-pressed={on}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${on ? 'translate-x-4' : 'translate-x-0.5'}`} />
    </button>
  );
}
